import React, { useEffect, useMemo, useState } from 'react';
import { Padding } from '@extensions/AttributePanel/components/attributes/Padding';
import { Height } from '@extensions/AttributePanel/components/attributes/Height';
import { ContainerBackgroundColor } from '@extensions/AttributePanel/components/attributes/ContainerBackgroundColor';
import { BorderWidth } from '@extensions/AttributePanel/components/attributes/BorderWidth';
import { BorderStyle } from '@extensions/AttributePanel/components/attributes/BorderStyle';
import { BorderColor } from '@extensions/AttributePanel/components/attributes/BorderColor';
import { LetterSpacing } from '@extensions/AttributePanel/components/attributes/LetterSpacing';
import { LineHeight } from '@extensions/AttributePanel/components/attributes/LineHeight';

import { AttributesPanelWrapper } from '@extensions/AttributePanel/components/attributes/AttributesPanelWrapper';
import { Collapse, Grid, Button } from '@arco-design/web-react';
import { t } from '@bay-email/core';
import { TextStyle, useFocusIdx } from '@bay-email/editor';
import { HtmlEditor } from '../../UI/HtmlEditor';
import { ClassName } from '../../attributes/ClassName';
import { pixelAdapter } from '../../adapter';
import { BsCodeSlash, BsChevronDown } from 'react-icons/bs';
import { BorderWidthControl } from '../shared/BorderWidthControl';
import { useSelectedNode } from '@extensions/hooks/useSelectedNode';
import { ResetInheritedTypography } from '../shared/ResetInheritedTypography';
import { AttachedListingSection } from '../shared/AttachedListingSection';
import { usesListingMergeTags } from '../shared/listingAttachment';
import { useClearAttachedListingWhenUnused } from '../shared/useClearAttachedListingWhenUnused';

export function Text() {
	const [visible, setVisible] = useState(false);
	const { selectedNode } = useSelectedNode();
	const textContent = String(selectedNode?.data?.value?.content || '');
	const hasListingMergeTagsInContent = useMemo(
		() => usesListingMergeTags(textContent),
		[textContent],
	);
	useClearAttachedListingWhenUnused(selectedNode, hasListingMergeTagsInContent);
	const [activeKeys, setActiveKeys] = useState<string[]>(() =>
		hasListingMergeTagsInContent ? ['-1', '0', '1', '4'] : ['0', '1', '4'],
	);
	const { focusIdx } = useFocusIdx();

	useEffect(() => {
		if (!hasListingMergeTagsInContent) return;

		setActiveKeys(keys => {
			if (keys.includes('-1')) return keys;
			return ['-1', ...keys];
		});
	}, [hasListingMergeTagsInContent]);

	const handleCollapseAll = React.useCallback(() => {
		const hasCollapsibleSectionsOpen = activeKeys.length > 0;

		if (hasCollapsibleSectionsOpen) {
			setActiveKeys([]);
		} else {
			setActiveKeys(
				hasListingMergeTagsInContent ? ['-1', '0', '1', '4'] : ['0', '1', '4'],
			);
		}
	}, [activeKeys, hasListingMergeTagsInContent]);

	return (
		<AttributesPanelWrapper
			onCollapseAll={handleCollapseAll}
			allCollapsed={activeKeys.length === 0}
		>
			<Collapse
				className='attribute-panel-collapse'
				accordion={false}
				activeKey={activeKeys}
				onChange={(key, keys) => {
					setActiveKeys((keys as string[]) || []);
				}}
				expandIconPosition='right'
				expandIcon={<BsChevronDown size='1rem' />}
			>
				{hasListingMergeTagsInContent && (
					<Collapse.Item
						contentStyle={{ padding: 0 }}
						name='-1'
						header={t('Listing Settings')}
					>
						<AttachedListingSection
							observedValues={[selectedNode?.data?.value?.content]}
						/>
					</Collapse.Item>
				)}
				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='0'
					header={t('Dimension')}
				>
					<div className='p-3'>
						<div style={{ marginBottom: '8px' }}>
							<Grid.Row gutter={16}>
								<Grid.Col span={12}>
									<Height config={pixelAdapter} />
								</Grid.Col>
							</Grid.Row>
						</div>
						<Padding showResetAll />
					</div>
				</Collapse.Item>
				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='1'
					header={t('Styling')}
				>
					<div className='p-3'>
						<ContainerBackgroundColor title={t('Background color')} />
						<LetterSpacing
							label={t('Letter Spacing (px)')}
							config={pixelAdapter}
						/>
						<LineHeight />
						<ResetInheritedTypography />

						<TextStyle
							variation='strong'
							style={{ marginBottom: '12px', marginTop: '16px', display: 'block' }}
						>
							{t('Border')}
						</TextStyle>
						<Grid.Row gutter={16}>
							<Grid.Col span={16}>
								<BorderColor secondary />
							</Grid.Col>
							<Grid.Col span={8}>
								<BorderStyle secondary />
							</Grid.Col>
						</Grid.Row>
						<BorderWidthControl />
					</div>
				</Collapse.Item>
				<Collapse.Item
					contentStyle={{ padding: 0 }}
					name='4'
					header={t('Extra')}
				>
					<div className='p-3'>
						<div style={{ marginBottom: 12 }}>
							<div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
								{t('HTML mode')}
							</div>
							<div
								style={{ fontSize: 12, color: 'var(--color-text-2)', marginBottom: 8 }}
							>
								{t('Open HTML editor for advanced text editing.')}
							</div>
							<Button
								type='primary'
								size='large'
								long
								onClick={() => setVisible(true)}
								icon={<BsCodeSlash style={{ fontSize: 18 }} />}
							>
								{t('Open HTML Editor')}
							</Button>
						</div>
						<ClassName />
					</div>
				</Collapse.Item>
			</Collapse>
			<HtmlEditor
				visible={visible}
				setVisible={setVisible}
			/>
		</AttributesPanelWrapper>
	);
}
